import type { PlanStockObject, PlanStockObjectInput } from "@/lib/gql/__generated__/graphql";

import { stockRemainNumZodSchema } from "@/components/features/_shared/validation-schema/validation-schema";

// Action の型定義
type ActionValue = {
	planID: string;
	date: string;
	isAvailable: boolean;
	remainNum: number;
};

export class PlanStockDiffStore {
	#diffState: ActionValue[] = [];

	constructor(
		private readonly planStockObjects: PlanStockObject[], //
		private readonly onSetIsValid: (isValid: boolean) => void,
		private readonly onSetIsDirty: (isDirty: boolean) => void,
	) {}

	/**
	 * 更新のあったデータを追加
	 * @param value
	 */
	readonly put = (value: ActionValue) => {
		this.#diffState = this.#diffState.filter((stateItem) => !this.#isSameActionValue(stateItem, value));
		this.#diffState.push(value);

		// render中に状態更新処理が走る場合があるので、遅延させている
		setTimeout(() => {
			this.onSetIsValid(this.isValid());
			this.onSetIsDirty(this.isDirty());
		}, 0);
	};

	readonly clear = () => {
		this.#diffState.length = 0;
	};

	readonly isDirty = () => {
		if (!this.#diffState.length) return false;

		const diffItems = this.#diffState;
		const planStockObjects = this.planStockObjects;

		const isDirty = diffItems.some((diffItem) => {
			const planStockObject = planStockObjects.find((planStockObject) => {
				return planStockObject.planID === diffItem.planID;
			});

			if (!planStockObject) return false;

			const sameStockItem = planStockObject.stocks.find((stock) => stock.date === diffItem.date);

			if (!sameStockItem) return false;

			return sameStockItem.remainNum !== diffItem.remainNum || sameStockItem.isAvailable !== diffItem.isAvailable;
		});

		return isDirty;
	};

	readonly isValid = () => {
		return this.#diffState.every((item) => stockRemainNumZodSchema.safeParse(item.remainNum).success);
	};

	readonly getCurrentPlanStock = (value: ActionValue) => {
		return this.#diffState.find((item) => this.#isSameActionValue(item, value));
	};

	/**
	 * サーバーに送信するデータを取得
	 * @returns
	 */
	readonly getDiffItems = (): Array<PlanStockObjectInput> => {
		const planIDs = this.#diffState.map((item) => item.planID);
		const notDuplicatePlanIDs = Array.from(new Set(planIDs));

		const diffItems = notDuplicatePlanIDs
			.map((planID) => {
				const stocks = this.#diffState
					.filter((stateItem) => stateItem.planID === planID) // 特定のプランにひもづくデータを取得
					.map((item) => ({
						date: item.date,
						remainNum: item.remainNum,
						isAvailable: item.isAvailable,
					}));
				return {
					planID,
					stocks,
				};
			})
			.flat();

		return diffItems;
	};

	#isSameActionValue = (a: ActionValue, b: ActionValue) => {
		if (a.planID !== b.planID) {
			return false;
		}

		if (a.date !== b.date) {
			return false;
		}

		return true;
	};
}
